/**
 * /login — email + password login (no LINE).
 *
 * Owner must first enable web login from inside LIFF
 * (ตั้งค่า → เข้าระบบผ่านเว็บ) before this works. Then this page lets
 * them sign in from any desktop browser.
 *
 * Same credential works on paypilot/jobpilot/evalpilot since all three
 * workers share the same Supabase web_identities table.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, KeyRound, LogIn, MessageCircle, Sparkles } from 'lucide-react';
import { apiFetch, ApiError } from '../lib/api-client';
import { useAuthStore } from '../lib/store';
import { login } from '../lib/liff';
import { Logo } from '../components/ui/Logo';
import type { AuthProfile } from '@payroll/shared';

export function WebLogin() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setBusy(true);
    setErr(null);
    try {
      const result = await apiFetch<{ jwt: string; profile: AuthProfile }>('/auth/web-login', {
        method: 'POST',
        body: { email: email.trim(), password },
        skipAuth: true,
      });
      setAuth(result.jwt, result.profile);
      // Land on the cross-app launcher first so the owner can pick which
      // product to work in. They can always come back from any app's
      // "เปลี่ยนแอป" button.
      navigate('/launcher', { replace: true });
    } catch (e2) {
      const msg = e2 instanceof ApiError ? e2.message : e2 instanceof Error ? e2.message : 'เข้าสู่ระบบไม่สำเร็จ';
      setErr(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <section className="w-full max-w-sm">
        <div className="text-center mb-6">
          <Logo wordmark />
        </div>
        <form onSubmit={handleSubmit} className="card p-6 space-y-4">
          <div className="space-y-1">
            <h1 className="text-lg font-semibold">เข้าสู่ระบบผ่านเว็บ</h1>
            <p className="text-xs text-gray-500">
              สำหรับผู้ใช้ที่ตั้งค่า email + รหัสผ่านไว้แล้ว (ตั้งจาก LINE: ตั้งค่า → เข้าระบบผ่านเว็บ)
            </p>
          </div>

          <div>
            <label className="label">อีเมล</label>
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                autoComplete="email"
                className="input pl-9"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                required
              />
            </div>
          </div>

          <div>
            <label className="label">รหัสผ่าน</label>
            <div className="relative">
              <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                autoComplete="current-password"
                className="input pl-9"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={8}
              />
            </div>
          </div>

          {err && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>
          )}

          <button type="submit" disabled={busy} className="btn-cta w-full">
            <LogIn className="w-4 h-4" />
            {busy ? 'กำลังเข้าสู่ระบบ…' : 'เข้าสู่ระบบ'}
          </button>

          <p className="text-xs text-center text-gray-500">
            ยังไม่ได้ตั้งรหัสผ่าน? ใช้ปุ่ม “เข้าสู่ระบบด้วย LINE” ด้านล่าง
          </p>
        </form>

        {/* ทางเลือกอื่น: LINE login + สมัครทดลองใช้ */}
        <div className="mt-4 space-y-2">
          <div className="flex items-center gap-3 text-xs text-gray-400">
            <span className="h-px flex-1 bg-gray-200" /> หรือ <span className="h-px flex-1 bg-gray-200" />
          </div>
          <button
            type="button"
            onClick={() => login(window.location.origin + '/')}
            className="w-full rounded-lg bg-[#06C755] px-4 py-2.5 text-sm font-semibold text-white hover:brightness-95 flex items-center justify-center gap-2"
          >
            <MessageCircle className="w-4 h-4" /> เข้าสู่ระบบด้วย LINE
          </button>
          <button
            type="button"
            onClick={() => navigate('/signup')}
            className="btn-secondary w-full"
          >
            <Sparkles className="w-4 h-4" /> สมัครทดลองใช้ฟรี
          </button>
          <p className="text-[11px] text-center text-gray-400">
            สมัครด้วยอีเมล + รหัสผ่าน ไม่ต้องผ่าน LINE · ปุ่ม “เข้าสู่ระบบด้วย LINE” สำหรับผู้ที่ผูก LINE ไว้แล้ว
          </p>
        </div>
      </section>
    </main>
  );
}
